import { v4 as uuidv4 } from "uuid";

import { mapNotNull } from "../../util/arrays";
import * as csv from "../../util/csv";
import {
  productIdColumnDescription,
  productCodeColumnDescription,
  productNameColumnDescription,
  quantitySoldColumnDescription,
  productPriceColumnDescription,
} from "./sales-data-csv-columns";

export interface ParsedSalesDataRow {
  id: string;
  productId: number | null;
  productCode: string | null;
  productName: string | null;
  quantitySold: number;
  pricePerItem: number | null;
  rowIndex: number;
}

export interface ParseSalesDataCsvOutput {
  parsedRows: Array<ParsedSalesDataRow>;
}

export function parseSalesDataCsv(text: string): ParseSalesDataCsvOutput {
  if (text === "") {
    return { parsedRows: [] };
  }

  const { headers, body } = csv.parse(text);
  const format = detectFormat(headers);

  const rows = body.filter((row) => !row.isEmpty());

  const parsedRows = mapNotNull(rows, (row, rowIndex) => {
    const productId = row.readOptionalNumber(format.productIdHeader);
    const productCode = row.readOptional(format.productCodeHeader);
    const productName = row.readOptional(format.productNameHeader);

    if (productId === null && productCode === null) {
      throw new Error(
        `Row ${rowIndex + 1}: Must include either Product ID or Product code`
      );
    }

    const quantitySold = row.readCompulsoryNumber(format.quantitySoldHeader, {
      canBeZero: true,
      canBeNegative: false,
    });
    const pricePerItem = row.readOptionalNumber(format.pricePerItemHeader, {
      canBeZero: true,
      canBeNegative: false,
    });

    return {
      id: uuidv4(),
      productId,
      productCode,
      productName,
      quantitySold,
      pricePerItem,
      rowIndex,
    };
  });

  return { parsedRows };
}

interface Format {
  productIdHeader: csv.Header | null;
  productCodeHeader: csv.Header | null;
  productNameHeader: csv.Header | null;
  quantitySoldHeader: csv.Header;
  pricePerItemHeader: csv.Header | null;
}

function detectFormat(headers: Array<string>): Format {
  const productIdHeader = csv.findHeaderOrNull(
    headers,
    productIdColumnDescription.columnNameCandidates
  );

  const productCodeHeader = csv.findHeaderOrNull(
    headers,
    productCodeColumnDescription.columnNameCandidates
  );

  if (productIdHeader === null && productCodeHeader === null) {
    throw new Error(
      "CSV must contain either a Product ID column or a Product code column"
    );
  }

  const productNameHeader = csv.findHeaderOrNull(
    headers,
    productNameColumnDescription.columnNameCandidates
  );
  const quantitySoldHeader = csv.findHeader(
    headers,
    quantitySoldColumnDescription.columnNameCandidates,
    "Sales"
  );
  const pricePerItemHeader = csv.findHeaderOrNull(
    headers,
    productPriceColumnDescription.columnNameCandidates
  );

  return {
    productIdHeader,
    productCodeHeader,
    productNameHeader,
    quantitySoldHeader,
    pricePerItemHeader,
  };
}
